import React, { useState, useEffect } from 'react';
import {
    Battery, BatteryCharging, Thermometer, HardDrive, Smartphone, Bluetooth, BluetoothOff, AlertTriangle, Camera, Scan,
    Fingerprint
} from 'lucide-react';
import { BatteryFull, BatteryLow, BatteryMedium, Loader } from 'lucide-react';


const warningMessages = {
    FACE_NOT_FOUND: "Face not detected. Please ensure your face is visible.",
    FACE_NOT_INSIDE_FRAME: "Face not fully in frame. Please center your face.",
    FACE_NOT_IN_OPTIMAL_DISTANCE: "Adjust distance. Move closer or further from the camera.",
    FACE_ORIENTATION_FAILED: "Face orientation incorrect. Please look directly at the camera.",
    FACE_ALIGNMENT_FAILED: "Face alignment is incorrect. Please look directly at the camera.",
    FINGERTIP_NOT_FOUND: "Fingertip not detected. Please place your finger on the camera.",
    FINGERTIP_BAD_QI: "Poor fingertip quality. Ensure finger covers camera completely."
};

const deviceStates = {
    BLE_CONNECTION: { color: '#3b82f6', text: 'BLE Connection' },
    READY: { color: '#10b981', text: 'Ready' },
    STARTED: { color: '#ef4444', text: 'Recording' },
    DETECTING: { color: '#8b5cf6', text: 'Detecting' },
    DONE: { color: '#059669', text: 'Done' },
    USER_ACTION_DONE: { color: '#059669', text: 'User Action Done' },
    MANAGER_ACTION_DONE: { color: '#059669', text: 'Manager Action Done' },
    BACKGROUND: { color: '#059669', text: 'Background Done' },
    CANCELLED: { color: 'RED', text: 'Cancelled' },
    POSITION: { color: '#3b82f6', text: 'Position' },
    CALIBRATION: { color: '#8b5cf6', text: 'Calibration' },
    QUALITY_ASSESS: { color: '#fbbf24', text: 'Quality Check' }
};

const FingertipVisualization = ({ fingertipData, deviceState }) => {
    if (!fingertipData) return null;

    const { detection, qualityAssess, mean } = fingertipData;

    // Use detection mean if available, otherwise use frame mean
    const rgbValues = detection?.mean || mean || { red: 0, green: 0, blue: 0 };
    const rgbColor = `rgb(${rgbValues.red}, ${rgbValues.green}, ${rgbValues.blue})`;

    // Get quality index if available
    const qi = qualityAssess?.qi;
    const qualityType = qualityAssess?.type;

    // Always show QI if available
    const showQualityInfo = qi != null;

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%'
        }}>
            {/* Simple color circle */}
            <div style={{
                width: '80px',
                height: '80px',
                backgroundColor: rgbColor,
                borderRadius: '50%',
                border: '2px solid #ccc',
                marginBottom: '15px'
            }}>
                {!detection && (
                    <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontSize: '10px',
                        color: '#fff',
                        textShadow: '0 0 2px #000',
                        textAlign: 'center'
                    }}>
                        Place finger
                    </div>
                )}
            </div>

            {/* Simple RGB values */}
            <div style={{
                fontSize: '12px',
                fontFamily: 'monospace',
                marginBottom: '10px'
            }}>
                <span style={{ color: '#ef4444' }}>R:{rgbValues.red}</span>
                {' '}
                <span style={{ color: '#10b981' }}>G:{rgbValues.green}</span>
                {' '}
                <span style={{ color: '#3b82f6' }}>B:{rgbValues.blue}</span>
            </div>

            {/* Simple QI values */}
            {showQualityInfo && (
                <div style={{
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    backgroundColor: qualityType === 'GOOD' ? '#d1fae5' : '#fee2e2',
                    padding: '4px 8px',
                    borderRadius: '4px'
                }}>
                    <span style={{ color: '#ef4444' }}>QI-R:{qi.red.toFixed(2)}</span>
                    {' '}
                    <span style={{ color: '#10b981' }}>QI-G:{qi.green.toFixed(2)}</span>
                    {' '}
                    <span style={{ color: '#3b82f6' }}>QI-B:{qi.blue.toFixed(2)}</span>
                </div>
            )}
        </div>
    );
};

const FaceTrackingAnimation = ({ faceData, warning, frameWidth, frameHeight }) => {
    const [faceProps, setFaceProps] = useState(null);

    useEffect(() => {
        if (faceData && faceData.detection) {
            const { rectangle } = faceData.detection;
            const { left, right, top, bottom } = rectangle;

            const faceWidth = right - left;
            const faceHeight = bottom - top;
            const centerX = (left + right) / 2;
            const centerY = (top + bottom) / 2;

            const scaleX = 90 / frameWidth;
            const scaleY = 160 / frameHeight;

            setFaceProps({
                x: centerX * scaleX,
                y: centerY * scaleY,
                width: faceWidth * scaleX,
                height: faceHeight * scaleY
            });
        } else {
            setFaceProps(null);
        }
    }, [faceData, frameWidth, frameHeight]);

    const getWarningColor = (warningType) => {
        if (warningType === 'FACE_NOT_FOUND' || warningType === 'FACE_NOT_INSIDE_FRAME' ||
            warningType === 'FINGERTIP_NOT_FOUND' || warningType === 'FINGERTIP_BAD_QI') {
            return 'red';
        }
        return 'yellow';
    };

    return (
        <svg
            viewBox="0 0 100 180"
            preserveAspectRatio="xMidYMid meet"
            style={{ width: '100%', height: '100%', border: '1px solid #ccc' }}
        >
            {faceProps && (
                <g transform="translate(5, 5)">
                    <rect
                        x={faceProps.x - faceProps.width / 2}
                        y={faceProps.y - faceProps.height / 2}
                        width={faceProps.width}
                        height={faceProps.height}
                        fill="none"
                        stroke="#808080"
                        strokeWidth="0.5"
                        style={{ transition: 'all 0.3s ease-in-out' }}
                    />
                    <ellipse
                        cx={faceProps.x}
                        cy={faceProps.y}
                        rx={faceProps.width * 0.3}
                        ry={faceProps.height * 0.35}
                        fill="#80808033"
                        stroke="#808080"
                        strokeWidth="0.5"
                        style={{ transition: 'all 0.3s ease-in-out' }}
                    />
                    <circle
                        cx={faceProps.x - faceProps.width * 0.15}
                        cy={faceProps.y - faceProps.height * 0.1}
                        r={faceProps.width * 0.05}
                        fill="#808080"
                        style={{ transition: 'all 0.3s ease-in-out' }}
                    />
                    <circle
                        cx={faceProps.x + faceProps.width * 0.15}
                        cy={faceProps.y - faceProps.height * 0.1}
                        r={faceProps.width * 0.05}
                        fill="#808080"
                        style={{ transition: 'all 0.3s ease-in-out' }}
                    />
                    <path
                        d={`M${faceProps.x - faceProps.width * 0.2} ${faceProps.y + faceProps.height * 0.15} 
               Q ${faceProps.x} ${faceProps.y + faceProps.height * 0.25}, 
                 ${faceProps.x + faceProps.width * 0.2} ${faceProps.y + faceProps.height * 0.15}`}
                        fill="none"
                        stroke="#808080"
                        strokeWidth="0.5"
                        style={{ transition: 'all 0.3s ease-in-out' }}
                    />
                </g>
            )}

            {warning && warning.type !== 'FACE_ORIENTAION_FAILED' && (
                <g>
                    <rect x="5" y="70" width="90" height="40" fill={getWarningColor(warning.type)} opacity="0.3">
                        <animate attributeName="opacity" values="0.3;0.1;0.3" dur="2s" repeatCount="indefinite" />
                    </rect>
                    <text x="50" y="90" textAnchor="middle" fill="black" fontSize="4" fontWeight="bold">
                        <tspan x="50" dy="0">{warningMessages[warning.type]?.split('.')[0]}</tspan>
                        <tspan x="50" dy="6">{warningMessages[warning.type]?.split('.')[1]}</tspan>
                    </text>
                </g>
            )}
        </svg>
    );
};
const DeviceStatusDashboard = ({
    deviceName,
    onSettingsChange,
    enableCameraSelection = true,
    enableBleSelection = true,
    statusData,
    measurementData,
    deviceState,
    deviceReason,
    camera,
    bleDevice,
    deviceMode,
    ppgDeviceOn = 'Right',
    showFaceTracking = true
}) => {
    const [deviceStateLocal, setDeviceStateLocal] = useState("")
    const [cameraType, setCameraType] = useState(camera);
    const [isBleOn, setIsBleOn] = useState(bleDevice);
    const [currentMeasurementData, setCurrentMeasurementData] = useState(null);
    const [deviceInfo, setDeviceInfo] = useState(null);
    const [mode, setMode] = useState(deviceMode);
    const [ppgDevice, setPpgDevice] = useState(ppgDeviceOn);

    useEffect(() => {
        if (deviceState === "DONE") {
            if (deviceReason === "USER_ACTION") {
                setDeviceStateLocal("USER_ACTION_DONE")
            } else if (deviceReason === "MANAGER_ACTION") {
                setDeviceStateLocal("DONE")
            } else if (deviceReason === "BACKGROUND") {
                setDeviceStateLocal("BACKGROUND")
            }
        } else if (deviceState === "CANCELLED") {
            setDeviceStateLocal("CANCELLED")
        } else if (deviceState) {
            setDeviceStateLocal(deviceState)
        }
    }, [deviceState, deviceReason])

    useEffect(() => {

        if (mode === 'Fingertip') {
            setCameraType('Back');
            onSettingsChange({ camera: 'Back', mode: 'Fingertip' });
        } else {
            onSettingsChange({ mode: 'Face' });
        }

    }, [mode]);

    useEffect(() => {
        if (measurementData) {
            setCurrentMeasurementData(measurementData);
        }
    }, [measurementData]);

    useEffect(() => {
        if (statusData) {
            setDeviceInfo(statusData);
        }
    }, [statusData]);


    const getBatteryIcon = (batteryLevel, status, isSaverMode) => {
        let IconComponent;

        if (status === 'CHARGING') {
            IconComponent = BatteryCharging;
        } else {
            if (batteryLevel > 80) {
                IconComponent = BatteryFull;
            } else if (batteryLevel > 40) {
                IconComponent = BatteryMedium;
            } else if (batteryLevel > 20) {
                IconComponent = BatteryLow;
            } else {
                IconComponent = Battery;
            }
        }

        const color = isSaverMode ? '#fbbf24' : '#10b981';

        return <IconComponent style={{ width: '1.25rem', height: '1.25rem', color: color }} />;
    };

    const getThermalIcon = (thermalStatus) => {
        let color;

        switch (thermalStatus) {
            case "LIGHT":
                color = '#10b981';
                break;
            case "MODERATE":
                color = '#fbbf24';
                break;
            case "SEVERE":
                color = '#ef4444';
                break;
            default:
                color = '#9ca3af';
                break;
        }

        return <Thermometer style={{ width: '1.25rem', height: '1.25rem', color: color }} />;
    };

    const handleCameraChange = (type) => {
        if (mode === 'Fingertip') return; // Prevent camera change in Fingertip mode
        setCameraType(type);
        onSettingsChange({ camera: type });
    };

    const handleModeToggle = (newMode) => {
        setMode(newMode);
    };

    const handleBleToggle = () => {
        const newBleState = !isBleOn;
        setIsBleOn(newBleState);
        const bleVar = newBleState ? 'On' : 'Off';
        onSettingsChange({ 'bleDevice': bleVar });

    };

    const handlePpgDeviceChange = (position) => {
        setPpgDevice(position);
        onSettingsChange({ 'ppgDeviceOn': position });
    };

    const showBleWarning = !enableBleSelection && isBleOn && currentMeasurementData?.state !== 'BLE_CONNECTION' && !currentMeasurementData?.isBLEDeviceConnected;
    const WarningBox = ({ type, message, color }) => (
        <div style={{
            padding: '0.5rem',
            backgroundColor: color === 'red' ? '#fee2e2' : '#fef3c7',
            color: color === 'red' ? '#b91c1c' : '#92400e',
            borderRadius: '0.5rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            fontSize: '1rem',
            textAlign: 'center',
            marginBottom: '0.5rem'
        }}>
            <AlertTriangle style={{
                width: '1.5rem',
                height: '1.5rem',
                marginBottom: '0.5rem',
                flexShrink: 0
            }} />
            <span style={{
                fontWeight: 'bold',
                fontSize: '1.25rem',
                marginBottom: '0.5rem',
                width: '100%',
                textAlign: 'center'
            }}>
                {type}
            </span>
            <span style={{
                fontSize: '1rem',
                textAlign: 'center',
                width: '100%'
            }}>
                {message}
            </span>
        </div>
    );
    const getWarningColor = (warningType) => {
        if (warningType === 'FACE_NOT_FOUND' || warningType === 'FACE_NOT_INSIDE_FRAME' ||
            warningType === 'FINGERTIP_NOT_FOUND' || warningType === 'FINGERTIP_BAD_QI') {
            return 'red';
        }
        return 'yellow';
    };

    const formatStorage = (megabytes) => {
        if (!megabytes) {
            return 'UNKNOWN'
        }
        if (megabytes >= 1024) {
            return (megabytes / 1024).toFixed(2) + ' GB';
        }
        return megabytes?.toFixed(0) + ' MB';
    };
    const getStorageColor = (deviceName, remainingSpaceMB) => {
        const isIphone = deviceName.toLowerCase().includes('iphone');
        const remainingSpaceGB = remainingSpaceMB / 1024;

        if (isIphone) {
            if (remainingSpaceGB > 50) return '#8b5cf6'; // purple for normal
            if (remainingSpaceGB > 20) return '#fbbf24'; // yellow for warning
            return '#ef4444'; // red for danger
        } else {
            if (remainingSpaceGB > 80) return '#8b5cf6'; // purple for normal
            if (remainingSpaceGB > 40) return '#fbbf24'; // yellow for warning
            return '#ef4444'; // red for danger
        }
    };

    // Determine whether to show face tracking or fingertip visualization
    const showFingertipViz = mode === 'Fingertip' && showFaceTracking;

    // Get current device state for fingertip visualization
    const currentDeviceState = deviceStateLocal || deviceState;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.75rem', backgroundColor: '#f3f4f6', borderRadius: '0.5rem', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', maxWidth: '100%' }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
                {(showFaceTracking || showFingertipViz) && (
                    <div style={{ width: '60%', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                        {!currentMeasurementData?.info && deviceStateLocal !== "CANCELLED" && deviceStateLocal !== "BACKGROUND" && deviceStateLocal !== "USER_ACTION_DONE" ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                <Loader style={{ width: '2rem', height: '2rem', color: '#3b82f6', animation: 'spin 1s linear infinite' }} />
                                <p style={{ marginTop: '1rem', textAlign: 'center' }}>Waiting for data...</p>
                            </div>
                        ) : (
                            <>
                                {showBleWarning && (
                                    <WarningBox
                                        type="PPG Device Disconnected"
                                        message="Please check your PPG device connection"
                                        color="red"
                                    />
                                )}

                                {/* Show face tracking or fingertip visualization based on mode */}
                                {mode === 'Face' ? (
                                    <FaceTrackingAnimation
                                        faceData={currentMeasurementData?.info}
                                        warning={currentMeasurementData?.warning}
                                        frameWidth={currentMeasurementData?.info?.width}
                                        frameHeight={currentMeasurementData?.info?.height}
                                    />
                                ) : (
                                    <FingertipVisualization
                                        fingertipData={currentMeasurementData?.info}
                                        deviceState={currentDeviceState}
                                    />
                                )}
                            </>
                        )}
                    </div>
                )}
                <div style={{ width: (showFaceTracking || showFingertipViz) ? '40%' : '100%', minWidth: 0, backgroundColor: 'white', padding: '0.75rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.25rem', fontSize: '0.875rem', fontWeight: 'bold', minHeight: '2.5em' }}>
                        <Smartphone style={{ width: '1.25rem', height: '1.25rem', color: '#3b82f6', flexShrink: 0, marginTop: '0.2em' }} />
                        <span style={{ wordBreak: 'break-word', hyphens: 'auto' }}>{deviceName}</span>
                    </div>
                    {deviceStateLocal && (
                        <div style={{
                            display: 'inline-block',
                            width: '100%',
                            backgroundColor: 'transparent',
                            border: `2px solid ${deviceStates[deviceStateLocal]?.color}`,
                            color: deviceStates[deviceStateLocal]?.color,
                            padding: '0.1rem 0.5rem',
                            borderRadius: '1rem',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            letterSpacing: '0.02em',
                            textAlign: 'center'
                        }}>
                            {deviceStates[deviceStateLocal]?.text}
                        </div>
                    )}
                    {deviceInfo && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.75rem' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                {getBatteryIcon(deviceInfo.batteryLevel, deviceInfo.batteryStatus, deviceInfo.isBatterySaverModeActive)}
                                <span style={{ marginTop: '0.25rem', textAlign: 'center' }}>{deviceInfo.batteryLevel}%</span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                {getThermalIcon(deviceInfo.thermalStatus)}
                                <span style={{ marginTop: '0.25rem', textAlign: 'center' }}>{deviceInfo.thermalStatus}</span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <HardDrive style={{ width: '1.25rem', height: '1.25rem', color: getStorageColor(deviceName, deviceInfo.remainingSpace) }} />
                                <span style={{ marginTop: '0.25rem', textAlign: 'center' }}>
                                    {formatStorage(deviceInfo.remainingSpace)}
                                </span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                {isBleOn ? (
                                    <Bluetooth style={{ width: '1.25rem', height: '1.25rem', color: '#3b82f6' }} />
                                ) : (
                                    <BluetoothOff style={{ width: '1.25rem', height: '1.25rem', color: '#9ca3af' }} />
                                )}
                                <span style={{ marginTop: '0.25rem', textAlign: 'center' }}>{isBleOn ? 'On' : 'Off'}</span>
                            </div>
                            {!enableCameraSelection && (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                    {mode === 'Face' ? (
                                        <Scan style={{ width: '1.25rem', height: '1.25rem', color: '#3b82f6' }} />
                                    ) : (
                                        <Fingerprint style={{ width: '1.25rem', height: '1.25rem', color: '#3b82f6' }} />
                                    )}
                                    <span style={{ marginTop: '0.25rem', textAlign: 'center' }}>{mode}</span>
                                </div>
                            )}
                            {!enableCameraSelection && (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                    <Camera style={{ width: '1.25rem', height: '1.25rem', color: '#3b82f6' }} />
                                    <span style={{ marginTop: '0.25rem', textAlign: 'center' }}>{cameraType === 'Front' ? 'Front' : 'Back'}</span>
                                </div>
                            )}
                            {isBleOn && (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                    <div style={{
                                        width: '1.25rem',
                                        height: '1.25rem',
                                        borderRadius: '50%',
                                        border: '2px solid #8b5cf6',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        fontSize: '0.7rem',
                                        fontWeight: 'bold',
                                        color: '#8b5cf6'
                                    }}>
                                        {ppgDevice === 'Left' ? 'L' : 'R'}
                                    </div>
                                    <span style={{ marginTop: '0.25rem', textAlign: 'center' }}>PPG: {ppgDevice}</span>
                                </div>
                            )}
                        </div>
                    )}
                    {(enableCameraSelection || enableBleSelection) && (
                        <div style={{
                            padding: '0.75rem',
                            backgroundColor: '#f1f5f9',
                            borderRadius: '0.75rem',
                            marginBottom: '0.75rem',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: '0.75rem'
                            }}>
                                {/* Mode Card */}
                                {enableCameraSelection && (
                                    <div style={{
                                        backgroundColor: 'white',
                                        borderRadius: '0.5rem',
                                        overflow: 'hidden',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                    }}>
                                        <div style={{
                                            padding: '0.5rem',
                                            backgroundColor: '#2563eb',
                                            fontSize: '0.75rem',
                                            fontWeight: 'bold',
                                            color: 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.35rem'
                                        }}>
                                            <Scan style={{ width: '0.8rem', height: '0.8rem', color: 'white' }} />
                                            MODE
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            height: '2.5rem'
                                        }}>
                                            <button
                                                onClick={() => handleModeToggle('Face')}
                                                style={{
                                                    flex: 1,
                                                    backgroundColor: mode === 'Face' ? '#3b82f6' : 'transparent',
                                                    color: mode === 'Face' ? 'white' : '#64748b',
                                                    border: 'none',
                                                    borderRight: '1px solid #e2e8f0',
                                                    cursor: 'pointer',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '500',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '0.35rem',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <Scan style={{ width: '0.8rem', height: '0.8rem' }} />
                                                Face
                                            </button>
                                            <button
                                                onClick={() => handleModeToggle('Fingertip')}
                                                style={{
                                                    flex: 1,
                                                    backgroundColor: mode === 'Fingertip' ? '#3b82f6' : 'transparent',
                                                    color: mode === 'Fingertip' ? 'white' : '#64748b',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '500',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '0.35rem',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <Fingerprint style={{ width: '0.8rem', height: '0.8rem' }} />
                                                Fingertip
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Camera Card */}
                                {enableCameraSelection && (
                                    <div style={{
                                        backgroundColor: 'white',
                                        borderRadius: '0.5rem',
                                        overflow: 'hidden',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                        opacity: mode === 'Fingertip' ? 0.7 : 1
                                    }}>
                                        <div style={{
                                            padding: '0.5rem',
                                            backgroundColor: '#059669',
                                            fontSize: '0.75rem',
                                            fontWeight: 'bold',
                                            color: 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.35rem'
                                        }}>
                                            <Camera style={{ width: '0.8rem', height: '0.8rem', color: 'white' }} />
                                            CAMERA
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            height: '2.5rem'
                                        }}>
                                            <button
                                                onClick={() => handleCameraChange('Front')}
                                                style={{
                                                    flex: 1,
                                                    backgroundColor: mode === 'Fingertip' ? 'transparent' :
                                                        (cameraType === 'Front' ? '#10b981' : 'transparent'),
                                                    color: mode === 'Fingertip' ? '#94a3b8' :
                                                        (cameraType === 'Front' ? 'white' : '#64748b'),
                                                    border: 'none',
                                                    borderRight: '1px solid #e2e8f0',
                                                    cursor: mode === 'Fingertip' ? 'not-allowed' : 'pointer',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '500',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '0.35rem',
                                                    transition: 'all 0.2s ease'
                                                }}
                                                disabled={mode === 'Fingertip'}
                                            >
                                                <Camera style={{ width: '0.8rem', height: '0.8rem' }} />
                                                Front
                                            </button>
                                            <button
                                                onClick={() => handleCameraChange('Back')}
                                                style={{
                                                    flex: 1,
                                                    backgroundColor: cameraType === 'Back' ? '#10b981' : 'transparent',
                                                    color: cameraType === 'Back' ? 'white' : '#64748b',
                                                    border: 'none',
                                                    cursor: mode === 'Fingertip' ? 'not-allowed' : 'pointer',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '500',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '0.35rem',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <Camera style={{ width: '0.8rem', height: '0.8rem' }} />
                                                Back
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Bluetooth Card */}
                                {enableBleSelection && (
                                    <div style={{
                                        backgroundColor: 'white',
                                        borderRadius: '0.5rem',
                                        overflow: 'hidden',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                    }}>
                                        <div style={{
                                            padding: '0.5rem',
                                            backgroundColor: '#0284c7',
                                            fontSize: '0.75rem',
                                            fontWeight: 'bold',
                                            color: 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.35rem'
                                        }}>
                                            <Bluetooth style={{ width: '0.8rem', height: '0.8rem', color: 'white' }} />
                                            BLUETOOTH
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            height: '2.5rem'
                                        }}>
                                            <button
                                                onClick={handleBleToggle}
                                                style={{
                                                    flex: 1,
                                                    backgroundColor: !isBleOn ? '#0ea5e9' : 'transparent',
                                                    color: !isBleOn ? 'white' : '#64748b',
                                                    border: 'none',
                                                    borderRight: '1px solid #e2e8f0',
                                                    cursor: 'pointer',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '500',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '0.35rem',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <BluetoothOff style={{ width: '0.8rem', height: '0.8rem' }} />
                                                Off
                                            </button>
                                            <button
                                                onClick={handleBleToggle}
                                                style={{
                                                    flex: 1,
                                                    backgroundColor: isBleOn ? '#0ea5e9' : 'transparent',
                                                    color: isBleOn ? 'white' : '#64748b',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '500',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '0.35rem',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <Bluetooth style={{ width: '0.8rem', height: '0.8rem' }} />
                                                On
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* PPG Device Card - Only visible when Bluetooth is on */}
                                {isBleOn && enableBleSelection && (
                                    <div style={{
                                        backgroundColor: 'white',
                                        borderRadius: '0.5rem',
                                        overflow: 'hidden',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                    }}>
                                        <div style={{
                                            padding: '0.5rem',
                                            backgroundColor: '#7c3aed',
                                            fontSize: '0.75rem',
                                            fontWeight: 'bold',
                                            color: 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.35rem'
                                        }}>
                                            <div style={{
                                                width: '0.8rem',
                                                height: '0.8rem',
                                                borderRadius: '50%',
                                                border: '1.5px solid white',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                fontSize: '0.5rem',
                                                fontWeight: 'bold',
                                                color: 'white'
                                            }}>
                                                P
                                            </div>
                                            PPG DEVICE
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            height: '2.5rem'
                                        }}>
                                            <button
                                                onClick={() => handlePpgDeviceChange('Left')}
                                                style={{
                                                    flex: 1,
                                                    backgroundColor: ppgDevice === 'Left' ? '#8b5cf6' : 'transparent',
                                                    color: ppgDevice === 'Left' ? 'white' : '#64748b',
                                                    border: 'none',
                                                    borderRight: '1px solid #e2e8f0',
                                                    cursor: 'pointer',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '500',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '0.35rem',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <div style={{
                                                    fontSize: '0.7rem',
                                                    fontWeight: 'bold',
                                                    width: '0.9rem',
                                                    height: '0.9rem',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    border: '1.5px solid',
                                                    borderRadius: '50%'
                                                }}>L</div>
                                                Left
                                            </button>
                                            <button
                                                onClick={() => handlePpgDeviceChange('Right')}
                                                style={{
                                                    flex: 1,
                                                    backgroundColor: ppgDevice === 'Right' ? '#8b5cf6' : 'transparent',
                                                    color: ppgDevice === 'Right' ? 'white' : '#64748b',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '500',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '0.35rem',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <div style={{
                                                    fontSize: '0.7rem',
                                                    fontWeight: 'bold',
                                                    width: '0.9rem',
                                                    height: '0.9rem',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    border: '1.5px solid',
                                                    borderRadius: '50%'
                                                }}>R</div>
                                                Right
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {(currentMeasurementData?.warning) && (
                        <WarningBox
                            type={warningMessages[currentMeasurementData.warning.type]?.split('.')[0]}
                            message={warningMessages[currentMeasurementData.warning.type]?.split('.')[1]}
                            color={getWarningColor(currentMeasurementData?.warning.type)}
                        />
                    )}

                    {/* Face position information (only show for face mode) */}
                    {(currentMeasurementData?.info?.position && mode === 'Face') && (
                        <div style={{
                            marginTop: '0.5rem',
                            fontSize: '0.75rem',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.25rem'
                        }}>
                            <div>
                                <strong>Distance:</strong> {currentMeasurementData.info.position.distance.type}
                                (Ratio: {currentMeasurementData.info.position.distance.ratio.toFixed(2)})
                            </div>
                            <div>
                                <strong>Orientation:</strong> {currentMeasurementData.info.position.orientation?.type}
                            </div>
                            <div>
                                <strong>Alignment:</strong> {currentMeasurementData?.info?.position?.alignment?.type}
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>

    );
};

export default DeviceStatusDashboard;